/**
 * Страница берётся из **своего модуля**, а не из barrel `@pages`: barrel стягивает все экраны в один
 * общий чанк и отменяет расщепление по маршрутам (EPIC-003).
 */
import { createFileRoute } from '@tanstack/react-router';

import { SharedI18n } from '@shared';

import { EvaluationPage } from '@pages/evaluation';
import { EvaluationModel } from '@units/evaluation';
import { IamService } from '@units/iam';

/**
 * `/evaluation` — только проводка (`rules/frontend-fsd.mdc` rule 10).
 *
 * Право одно на все три операции замера — `evaluation_run:read`, — и оно же стоит здесь. Раздел не
 * секрет внутри организации: отказ отдаётся `forbidden`, а не `not-found`, потому что коллега рядом
 * видит пункт в меню, и «ничего нет» читалось бы как поломка (`require-permission.guard.ts`).
 *
 * Схема поиска — состояние экрана, и проверяется она здесь, чтобы до компонента не доехал адрес,
 * набранный руками: недописанный UUID, посторонний параметр, путь вместо идентификатора набора.
 * Каждое такое значение откатывается схемой к «не выбрано», а не заменяет экран границей ошибки;
 * сервер поступает наоборот и отвечает 422, потому что там это означает, что клиент такое послал.
 *
 * **Loader есть, и данных он не грузит.** Прежняя редакция этого абзаца говорила «loader'а нет», и
 * довод был верен: три запроса экрана рисуют свои состояния сами, и ни один не обязателен для
 * первого кадра. Изменилось не это, а то, откуда берётся русский словарь: `evaluation.json` — 3,27 кБ
 * gzip, которые первая загрузка везла всем ради экрана за правом `evaluation_run:read`. Теперь он
 * приезжает своим чанком, и `loader` — то место, где его ждут: роутер не отрисует маршрут, пока
 * `loader` не закончил, поэтому экран появляется переведённым, а не набранным ключами. Границы
 * ожидания и ошибки по-прежнему наследуются от роутера — своих этому маршруту не нужно.
 *
 * **Крошка — `nav.evaluation`, а не `evaluation.title`.** Ожидающий матч попадает в `useMatches()`
 * вместе со `staticData` уже через `defaultPendingMs`, то есть `RouteAnnouncer` пишет `t(crumbKey)`
 * в заголовок вкладки **до** того, как `loader` закончил (проверено прогоном роутера, а не выведено).
 * Ключ из отложенного словаря в этой позиции — сырой ключ в заголовке вкладки. `nav.evaluation` —
 * та же строка слово в слово, что была в `evaluation.title`, в обоих языках, и она в первой
 * загрузке; дубликат снят, как это уже сделано у `/acts` (`pages/acts/page.tsx` берёт `nav.acts`).
 */
export const Route = createFileRoute('/_authenticated/evaluation')({
  beforeLoad: IamService.IamGuards.requirePermission({
    permission: 'evaluation_run:read',
    whenDenied: 'forbidden',
  }),
  validateSearch: EvaluationModel.evaluationSearchSchema,
  loader: () => SharedI18n.loadCatalogue('evaluation'),
  component: EvaluationPage,
  staticData: { crumbKey: 'nav.evaluation' },
});
